import React, { useState, useMemo } from 'react';
import {
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Box,
  Tooltip,
  useTheme,
} from '@mui/material';
import { lighten, type Theme } from '@mui/material/styles';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DriverDetails as DriverDetailsType } from '../types/predictions';
import { RaceCharts } from './charts/RaceCharts';
import { useQuery } from '@tanstack/react-query';
import { DriverDetailResponse, SimDriverResult, SimModel } from '../services/api';
import { maxScreenHeight } from '../utils/viewport';
import { ModalHeader, modalScrollBody, modalScrollPaper, useScrolledDown } from './ModalHeader';

// Color constants - matching predictions table
const COLORS = {
  max: "#62BF7B", // Green
  mid: "#FDE883", // Yellow
  min: "#F9696D", // Red
};

// Helper function to interpolate between two colors
const interpolateColor = (color1: string, color2: string, factor: number) => {
  const result = color1.match(/\w\w/g)?.map((c, i) => {
    const c1 = parseInt(c, 16);
    const c2 = parseInt(color2.match(/\w\w/g)![i], 16);
    const value = Math.round(c1 + factor * (c2 - c1));
    return value.toString(16).padStart(2, '0');
  });
  return `#${result?.join('')}`;
};

/** A touch brighter than the modal's header, the same relationship as the Simulator table's rows vs. its header. */
export const getModalBodyBg = (theme: Theme) =>
  theme.palette.mode === 'dark' ? lighten(theme.palette.background.paper, 0.05) : theme.palette.background.paper;

interface DriverDetailsModalProps {
  open: boolean;
  onClose: () => void;
  driversDetails: Record<string, DriverDetailsType[]>;
  isLoading: boolean;
  title?: string;
  subtitle?: string;
  /** Optional content shown between the title and the race table (e.g. simulation stats). */
  summary?: React.ReactNode;
  /** Show the race-by-race chart above the table (and the simulated track's key for its "This track" filter). */
  chart?: boolean;
  trackKey?: string;
  /** The Simulator's model, for the chart's "vs model" comparison. */
  model?: SimModel;
  /** The simulation's result for this driver and the whole field, for the Finish odds tab. */
  finishOdds?: SimDriverResult[];
  field?: SimDriverResult[];
  nSims?: number;
}

interface Column {
  id: string;
  label: string;
  field: keyof DriverDetailsType;
  format?: (value: any) => string | number;
  getColor?: (value: any) => string | undefined;
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const SortableHeader: React.FC<{ column: Column }> = ({ column }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
    fontWeight: 'bold',
    whiteSpace: 'pre-line',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  };

  return (
    <TableCell 
      ref={setNodeRef} 
      sx={style}
      {...attributes} 
      {...listeners}
    >
      {column.label}
    </TableCell>
  );
};

const getStatColor = (field: keyof DriverDetailsType, value: number) => {
  // Position-based stats (lower is better)
  if (['St', 'Pos', 'Mid', 'High', 'Low', 'Avg'].includes(field)) {
    // For positions: 1 (green) to 40 (red)
    const normalizedValue = Math.min(Math.max(value, 1), 40);
    const factor = (normalizedValue - 1) / 39; // 39 is the range (40 - 1)
    
    if (factor <= 0.5) {
      // Interpolate between green and yellow for first half
      return interpolateColor(COLORS.max, COLORS.mid, factor * 2);
    } else {
      // Interpolate between yellow and red for second half
      return interpolateColor(COLORS.mid, COLORS.min, (factor - 0.5) * 2);
    }
  }
  // Percentage stats and driver rating (higher is better)
  if (field.startsWith('Pct') || field === 'DriverRating') {
    const normalizedValue = field === 'DriverRating' 
      ? Math.min(Math.max(value, 0), 150) / 150 
      : Math.min(Math.max(value, 0), 1);
    
    if (normalizedValue <= 0.5) {
      // Interpolate between red and yellow for first half
      return interpolateColor(COLORS.min, COLORS.mid, normalizedValue * 2);
    } else {
      // Interpolate between yellow and green for second half
      return interpolateColor(COLORS.mid, COLORS.max, (normalizedValue - 0.5) * 2);
    }
  }
  return undefined;
};

interface RaceHistoryTableProps {
  driversDetails: Record<string, DriverDetailsType[]>;
  isLoading: boolean;
}

/** Race-by-race table for one or more drivers, grouped and numbered by race date (newest first). */
export const RaceHistoryTable: React.FC<RaceHistoryTableProps> = ({ driversDetails, isLoading }) => {
  const theme = useTheme();
  const bodyBg = getModalBodyBg(theme);
  const [columns, setColumns] = useState<Column[]>([
    { id: 'date', label: 'Date', field: 'Date', format: formatDate },
    { id: 'driver', label: 'Driver', field: 'Site' },
    { id: 'track', label: 'Track', field: 'Site' },
    {
      id: 'simWeight',
      label: 'Sim',
      field: 'SimWeight',
      format: (v: number) => `${(v * 100).toFixed(1)}%`,
    },
    { 
      id: 'start', 
      label: 'Start', 
      field: 'St',
      getColor: (value) => getStatColor('St', value)
    },
    { 
      id: 'finish', 
      label: 'Finish', 
      field: 'Pos',
      getColor: (value) => getStatColor('Pos', value)
    },
    { 
      id: 'rating', 
      label: 'Rating', 
      field: 'DriverRating', 
      format: (v: number) => Math.round(v),
      getColor: (value) => getStatColor('DriverRating', value)
    },
    { 
      id: 'avgPos', 
      label: 'Avg Pos', 
      field: 'Avg', 
      format: (v: number) => v.toFixed(1),
      getColor: (value) => getStatColor('Avg', value)
    },
    {
      id: 'top15Pct',
      label: '%T15',
      field: 'PctT15Lap',
      format: (v: number) => `${Math.round(v * 100)}%`,
      getColor: (value) => getStatColor('PctT15Lap', value)
    },
    { 
      id: 'lapsLed', 
      label: 'Laps Led', 
      field: 'Led'
    },
  ]);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setColumns((prevColumns: Column[]) => {
        const oldIndex = prevColumns.findIndex((col: Column) => col.id === active.id);
        const newIndex = prevColumns.findIndex((col: Column) => col.id === over.id);
        return arrayMove(prevColumns, oldIndex, newIndex);
      });
    }
  };

  // The Sim Weight column only applies to Simulator race histories.
  const hasSimWeight = useMemo(
    () => Object.values(driversDetails).some((races) => races.some((r) => r.SimWeight !== undefined)),
    [driversDetails]
  );
  const visibleColumns = useMemo(
    () => columns.filter((col) => col.id !== 'simWeight' || hasSimWeight),
    [columns, hasSimWeight]
  );

  // Combine and sort all races, grouped by date
  const sortedRacesByDate = useMemo(() => {
    const allRaces: Array<{ driverName: string; race: DriverDetailsType }> = [];
    
    Object.entries(driversDetails).forEach(([driverName, races]) => {
      races.forEach(race => {
        allRaces.push({ driverName, race });
      });
    });

    // Sort by date and finish position
    const sorted = allRaces.sort((a, b) => {
      const dateA = new Date(a.race.Date).getTime();
      const dateB = new Date(b.race.Date).getTime();
      
      // First sort by date (descending)
      if (dateB !== dateA) {
        return dateB - dateA;
      }
      
      // For same date, sort by finish position (ascending)
      return a.race.Pos - b.race.Pos;
    });

    // Group by date
    const groups: Record<string, Array<{ driverName: string; race: DriverDetailsType }>> = {};
    sorted.forEach(raceData => {
      const date = raceData.race.Date;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(raceData);
    });

    return Object.entries(groups);
  }, [driversDetails]);

  return (
    <>
      {isLoading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '200px'
        }}>
          <CircularProgress />
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      width: '36px',
                      backgroundColor: bodyBg,
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    #
                  </TableCell>
                  <SortableContext
                    items={visibleColumns.map(col => col.id)}
                    strategy={horizontalListSortingStrategy}
                  >
                    {visibleColumns.map((column) => (
                      <SortableHeader key={column.id} column={column} />
                    ))}
                  </SortableContext>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRacesByDate.map(([date, races], groupIndex) => (
                  <React.Fragment key={date}>
                    {races.map(({ driverName, race }, raceIndex, raceArray) => {
                      // Calculate the race number (counting up from 1 for most recent)
                      let raceNumber = 1;
                      // Add 1 for each previous date group
                      raceNumber += groupIndex;
                    
                      return (
                        <TableRow 
                          key={`${driverName}-${race.Date}-${raceIndex}`}
                          sx={{
                            backgroundColor: groupIndex % 2 === 0
                              ? (theme: Theme) =>
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.04)'
                                    : 'rgba(0, 0, 0, 0.04)'
                              : (theme: Theme) =>
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.12)'
                                    : 'rgba(0, 0, 0, 0.12)',
                            // Add top border to first row of each date group
                            ...(raceIndex === 0 && groupIndex !== 0 && {
                              'td': {
                                borderTop: '2px solid black',
                              }
                            }),
                          }}
                        >
                          <TableCell
                            sx={{
                              backgroundColor: bodyBg,
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              borderRight: '1px solid rgba(224, 224, 224, 1)',
                              fontWeight: 'medium',
                            }}
                          >
                            {raceIndex === 0 ? raceNumber : ''}
                          </TableCell>
                          {visibleColumns.map((column) => {
                            if (column.id === 'simWeight') {
                              return (
                                <TableCell key={column.id} sx={{ whiteSpace: 'nowrap' }}>
                                  {race.SimWeight !== undefined && column.format ? column.format(race.SimWeight) : ''}
                                  {race.DNF ? ' · DNF' : ''}
                                  {race.TrackAdj && (
                                    <Tooltip title="Also feeds the track-specific adjustment (this track or one of its track-specific matches)">
                                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                        {' · track adj'}
                                      </Box>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              );
                            }
                            if (column.id === 'driver') {
                              return (
                                <TableCell 
                                  key={column.id}
                                  sx={{ fontWeight: 'bold' }}
                                >
                                  {driverName}
                                </TableCell>
                              );
                            }
                          
                            const value = race[column.field];
                            return (
                              <TableCell 
                                key={column.id}
                                sx={{ 
                                  bgcolor: column.getColor ? column.getColor(value) : undefined,
                                  color: column.getColor ? 'black' : undefined
                                }}
                              >
                                {column.format ? column.format(value) : value}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DndContext>
      )}
    </>
  );
};

export const DriverDetailsModal: React.FC<DriverDetailsModalProps> = ({
  open,
  onClose,
  driversDetails,
  isLoading,
  title = 'Race History Comparison',
  subtitle,
  summary,
  chart = false,
  trackKey,
  model,
  finishOdds,
  field,
  nSims,
}) => {
  const theme = useTheme();
  const bodyBg = getModalBodyBg(theme);
  const [scrolled, onScroll] = useScrolledDown();
  return (
  <Modal open={open} onClose={onClose}>
    <Paper sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '95%',
      maxWidth: 1400,
      ...maxScreenHeight(90),
      ...modalScrollPaper,
      p: 0,
    }}>
      <ModalHeader title={title} onClose={onClose} compact={scrolled} />
      <Box onScroll={onScroll} sx={{ ...modalScrollBody, bgcolor: bodyBg, px: 3, pt: 2, pb: 3, borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
        {summary && <Box sx={{ mb: 2 }}>{summary}</Box>}
        {chart && !isLoading && (
          <>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Data explorer
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <RaceCharts
                driversDetails={driversDetails}
                trackKey={trackKey}
                model={model}
                finishOdds={finishOdds}
                field={field}
                nSims={nSims}
              />
            </Paper>
          </>
        )}
        <Typography variant="h6" sx={{ mb: 1 }}>
          Race by race
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {subtitle}
          </Typography>
        )}
        <RaceHistoryTable driversDetails={driversDetails} isLoading={isLoading} />
      </Box>
    </Paper>
  </Modal>
  );
};

export const useDriverDetails = (driverNames: string[], trackNames: string[], year: number) => {
  return useQuery({
    queryKey: ['driverDetails', driverNames, trackNames, year],
    queryFn: async () => {
      const response = await fetch('/api/stats/driver-details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          drivers: driverNames,
          trackNames,
          year,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch driver details');
      }

      const data: DriverDetailResponse[] = await response.json();

      // Group the results by driver
      const groupedData: Record<string, DriverDetailsType[]> = {};
      data.forEach((detail: DriverDetailResponse) => {
        const driverName = detail.Driver;
        if (!groupedData[driverName]) {
          groupedData[driverName] = [];
        }
        const { Driver, ...raceDetails } = detail;
        groupedData[driverName].push(raceDetails);
      });

      return groupedData;
    },
    enabled: driverNames.length > 0 && trackNames.length > 0 && !!year
  });
};

export interface DriverDetails {
  Date: string;
  Site: string;
  St: number;
  Pos: number;
  DriverRating: number;
  Mid: number;
  High: number;
  Low: number;
  Avg: number;
  PassDif: number;
  GfPass: number;
  GfPassed: number;
  QPass: number;
  PctQPass: number;
  Fastest: number;
  T15Lap: number;
  PctT15Lap: number;
  Led: number;
  PctLed: number;
  Total: number;
} 


